import axios, {AxiosRequestConfig} from 'axios'
import {Dispatch, SetStateAction, useCallback, useState} from 'react'
import {JsonData} from 'utilities/types'

export interface DeleteDataResponse<TData = unknown> {
  loading: boolean
  error: Error | undefined
  data: TData | undefined
  setData: Dispatch<SetStateAction<TData | undefined>>
}

export interface DeleteDataOptions<TResponse = unknown> {
  onComplete?: (res: TResponse) => void
  onError?: (e: any) => void
  excludeAuthHeaders?: boolean
  loading?: boolean
  page?: number
  perPage?: number
}

export async function deleteData<TResponse = unknown>(
  path: string,
  config?: AxiosRequestConfig<any>
): Promise<TResponse & JsonData> {
  const res = await axios.delete(path, config ?? {})
  return res.data as unknown as TResponse & JsonData
}

export default function useDeleteData<TResponse = unknown>(
  path: string,
  headerArgs?: Record<string, string>
): [(options?: DeleteDataOptions<TResponse>) => Promise<void>, DeleteDataResponse<TResponse>] {
  const [data, setData] = useState<TResponse | undefined>(undefined)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | undefined>()

  const execDeleteData = useCallback(
    async (options?: DeleteDataOptions<TResponse>) => {
      setLoading(true)
      try {
        deleteData(path)
          .then((response) => {
            setLoading(false)
            setData(response as TResponse)
            options?.onComplete?.(response as TResponse)
          })
          .catch((e) => {
            setData(undefined)
            setLoading(false)
            setError(e)
            options?.onError?.(e)
          })
      } catch (e) {
        setError(e as Error)
        setLoading(false)
        options?.onError?.(e)
      }
    },
    [headerArgs, path]
  )

  return [execDeleteData, {data, loading, error, setData}]
}
